import { alpha } from "@mui/material/styles";

// ----------------------------------------------------------------------

export type ColorSchema =
  | "primary"
  | "secondary"
  | "info"
  | "success"
  | "warning"
  | "error";

declare module "@mui/material/styles/createPalette" {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
}

// SETUP COLORS

export const grey = {
  0: "#FFFFFF",
  100: "#F9FAFB",
  200: "#F4F6F8",
  300: "#DFE3E8",
  400: "#C4CDD5",
  500: "#919EAB",
  600: "#637381",
  700: "#454F5B",
  800: "#212B36",
  900: "#161C24",
};

export const primary = {
  /*GREEN*/ lighter: "#EFD6FF",
  light: "#3B9C91",
  main: "#007B77",
  dark: "#105B59",
  darker: "#0B4A4E",
  contrastText: "#FFFFFF",
};

export const secondary = {
  /*ORANGE*/ lighter: "#FAFBFC",
  light: "#FD7841",
  main: "#FD5210",
  dark: "#D9552F",
  darker: "#B63820",
  contrastText: "#FFFFFF",
};

export const info = {
  lighter: "#CAFDF5",
  light: "#61F3F3",
  main: "#00B8D9",
  dark: "#006C9C",
  darker: "#003768",
  contrastText: "#FFFFFF",
};

export const success = {
  lighter: "#D3FCD2",
  light: "#77ED8B",
  main: "#22C55E",
  dark: "#118D57",
  darker: "#065E49",
  contrastText: "#ffffff",
};

export const warning = {
  lighter: "#FFF5CC",
  light: "#FFD666",
  main: "#FFAB00",
  dark: "#B76E00",
  darker: "#7A4100",
  contrastText: grey[800],
};

export const error = {
  lighter: "#FFE9D5",
  light: "#E07A43",
  main: "#CC440E",
  dark: "#AF2D0A",
  darker: "#921B07",
  contrastText: "#FFFFFF",
};

export const common = {
  black: "#000000",
  white: "#FFFFFF",
};

// FS-2026-066: coordinated portal colours; persisted through the existing theme fields.
const sportyPortalPresets = new Set(["electric", "neon", "stadium", "racing", "tropical", "power", "turbo", "sprint"]);
export const portalColorPresets = [
  ["classic", "#1976d2", "#9c27b0", common.white],
  ["teal", primary.main, secondary.darker, "#F2F8F7"],
  ["forest", "#226342", "#91651C", "#F4F8F3"],
  ["emerald", "#087F5B", "#7353A6", "#F1FAF6"],
  ["jade", "#17766B", "#A7425B", "#F2F9F7"],
  ["ocean", "#17657D", "#A75B13", "#F2F8FB"],
  ["azure", "#1769AA", "#9B4537", "#F2F7FC"],
  ["cobalt", "#2A4B9B", "#8A6A1A", "#F4F6FC"],
  ["navy", "#17345B", "#9A5434", "#F5F7FA"],
  ["indigo", "#3F3E8C", "#25705A", "#F5F4FC"],
  ["electric", "#0047FF", "#B800E6", "#F6F2FF"],
  ["violet", "#70428C", "#316F68", "#FAF5FC"],
  ["plum", "#713E67", "#346B87", "#FAF4F8"],
  ["neon", "#6D28D9", "#D0006F", "#FFF4FB"],
  ["rose", "#A13E62", "#326B75", "#FCF4F7"],
  ["raspberry", "#A22B52", "#5D5B93", "#FDF3F6"],
  ["cherry", "#A52A36", "#316A5A", "#FDF4F4"],
  ["terracotta", "#A4492F", "#366B6B", "#FCF5F0"],
  ["copper", "#95502D", "#396483", "#FBF6F1"],
  ["stadium", "#D52B00", "#6320C4", "#FFF5ED"],
  ["olive", "#646E2B", "#80516E", "#F7F9EF"],
  ["racing", "#CF003D", "#0057D9", "#F4F7FF"],
  ["pine", "#275E50", "#855CA1", "#F0F7F4"],
  ["slate", "#475569", "#8A4C66", "#F4F6F8"],
  ["graphite", "#343C46", "#326D82", "#F6F7F8"],
  ["tropical", "#007A45", "#C30087", "#F1FFF4"],
  ["power", "#5800D4", "#B84A00", "#FFF5FF"],
  ["arctic", "#2B6673", "#65508C", "#F0F8FA"],
  ["turbo", "#3020CC", "#D00038", "#FFF4F2"],
  ["sprint", "#0066CA", "#C53300", "#F1FAFF"],
].map(([id, primaryColor, secondaryColor, backgroundColor]) => ({
  id,
  primary_color: primaryColor,
  secondary_color: secondaryColor,
  background_color: backgroundColor,
  sporty: sportyPortalPresets.has(id),
}));

export const action = {
  hover: alpha(grey[500], 0.08),
  selected: alpha(grey[500], 0.16),
  disabled: alpha(grey[500], 0.8),
  disabledBackground: alpha(grey[500], 0.24),
  focus: alpha(grey[500], 0.24),
  hoverOpacity: 0.08,
  disabledOpacity: 0.48,
};
export const lightgreybackground = { v1: "#F9FAFB" };

const base = {
  primary,
  secondary,
  info,
  success,
  warning,
  error,
  grey,
  common,
  divider: alpha(grey[500], 0.2),
  action,
  lightgreybackground,
};

// ----------------------------------------------------------------------

export function palette(mode: "light" | "dark") {
  const light = {
    ...base,
    mode: "light",
    text: {
      primary: grey[800],
      secondary: grey[600],
      disabled: grey[500],
    },
    background: {
      paper: "#FFFFFF",
      default: "#FFFFFF",
      neutral: grey[200],
    },
    action: {
      ...base.action,
      active: grey[600],
    },
  };

  const dark = {
    ...base,
    mode: "dark",
    text: {
      primary: "#FFFFFF",
      secondary: grey[500],
      disabled: grey[600],
    },
    background: {
      paper: grey[800],
      default: grey[900],
      neutral: alpha(grey[500], 0.12),
    },
    action: {
      ...base.action,
      active: grey[500],
    },
  };

  return mode === "light" ? light : dark;
}
